import { RudderEvent } from 'constants/rudderEvents'
import { useIsOsanoCmpActive } from 'context/common/OsanoCmpContext'
import {
  OsanoConsent,
  subscribeToOsanoConsentEvents,
} from 'lib/osanoAnalyticsConsent'
import {
  buildConsentSnapshotPayload,
  isInitializedSnapshotSent,
  markInitializedSnapshotSent,
} from 'lib/osanoConsentEventPayloads'
import { useEffect, useRef } from 'react'
import logEvent from 'utils/logEvent'

type PendingOsanoLogEvent = {
  eventName: RudderEvent
  data: Record<string, unknown>
}

function removeBlockedScriptFromBuffer(src: string): void {
  const buffered = window.__twOsanoBlockedScripts

  if (!buffered?.length) {
    return
  }

  window.__twOsanoBlockedScripts = buffered.filter((entry) => entry !== src)
}

/** True when Osano's cookie consent bar is present and visible in the DOM. */
function isOsanoBannerVisible(): boolean {
  if (typeof document === 'undefined') {
    return false
  }

  const dialog = document.querySelector('.osano-cm-dialog')

  if (!dialog) {
    return false
  }

  const style = window.getComputedStyle(dialog)

  return style.display !== 'none' && style.visibility !== 'hidden'
}

/**
 * Subscribes to Osano consent events and forwards them to RudderStack via
 * logEvent.  Most events are gated: Osano must be active for this user AND
 * ANALYTICS must be ACCEPT at fire time.
 *
 * Exception: `osano_banner_displayed` is detected when the banner is shown
 * (implicit or strict) and buffered until RudderStack is ready — otherwise it
 * never fires on React for implicit consent (THRIV-15356).
 *
 * Mount this hook in PagesAnalyticsShell (_app.tsx) and
 * AppRouterAnalyticsShell (AppRouterWrapper.tsx) next to useHeapInit.
 *
 * @param analyticsConsentGranted — value from useRudderStackInit; when false
 *   the hook no-ops and cleans up its listeners.
 * @param rudderStackLoaded — when false, buffered events wait until RudderStack
 *   is ready (first-grant race with SDK load).
 */
const useOsanoConsentEvents = (
  analyticsConsentGranted: boolean,
  rudderStackLoaded: boolean
): void => {
  const isOsanoCmpActive = useIsOsanoCmpActive()

  const analyticsConsentGrantedRef = useRef(analyticsConsentGranted)
  const rudderStackLoadedRef = useRef(rudderStackLoaded)

  // Track whether initialized fired with undefined consent in this page
  // lifetime.  Used to set is_new_consent on the snapshot.
  const initializedWithNullConsent = useRef(false)

  // Once per page lifetime — avoid double-firing from init + ui-changed.
  const bannerDisplayedSent = useRef(false)

  // Per consent-grant cycle: flush early script-blocked buffer once per ACCEPT.
  const scriptBufferFlushedForGrant = useRef(false)

  const pendingConsentSavedEventsRef = useRef<PendingOsanoLogEvent[]>([])

  useEffect(() => {
    analyticsConsentGrantedRef.current = analyticsConsentGranted

    if (!analyticsConsentGranted) {
      scriptBufferFlushedForGrant.current = false
      // Discard anything buffered before delivery if consent was revoked.
      pendingConsentSavedEventsRef.current = []
    }
  }, [analyticsConsentGranted])

  /**
   * Forward to logEvent. When onTracked is provided, only invoke it after a
   * real Rudder SDK handoff (not when retries are exhausted with no SDK).
   */
  const deliverOsanoEvent = (
    eventName: RudderEvent,
    data: Record<string, unknown>,
    onTracked?: () => void
  ): void => {
    if (!onTracked) {
      logEvent(eventName, data)

      return
    }

    logEvent(eventName, data, undefined, () => {
      // logEvent invokes onTracked both on successful track and after retries
      // are exhausted — only treat as delivered when the SDK is present.
      if (globalThis.rudderanalytics) {
        onTracked()
      }
    })
  }

  useEffect(() => {
    rudderStackLoadedRef.current = rudderStackLoaded

    if (!rudderStackLoaded) {
      return
    }

    const pending = pendingConsentSavedEventsRef.current

    if (pending.length === 0) {
      return
    }

    pendingConsentSavedEventsRef.current = []

    // Re-check consent immediately before flush delivery. If the visitor
    // revoked while events were buffered, drop them.
    if (!analyticsConsentGrantedRef.current) {
      return
    }

    pending.forEach(({ eventName, data }) => {
      deliverOsanoEvent(eventName, data)
    })
  }, [rudderStackLoaded])

  const trackBufferedEvent = (
    eventName: RudderEvent,
    data: Record<string, unknown>
  ): void => {
    if (!rudderStackLoadedRef.current) {
      pendingConsentSavedEventsRef.current.push({ eventName, data })

      return
    }

    deliverOsanoEvent(eventName, data)
  }

  const emitBannerDisplayedOnce = (): void => {
    if (bannerDisplayedSent.current) {
      return
    }

    bannerDisplayedSent.current = true
    const currentConsent = window.Osano?.cm?.getConsent?.() ?? {}
    trackBufferedEvent(RudderEvent.OSANO_BANNER_DISPLAYED, currentConsent)
  }

  useEffect(() => {
    if (!isOsanoCmpActive || !analyticsConsentGranted) {
      return
    }

    if (!scriptBufferFlushedForGrant.current) {
      scriptBufferFlushedForGrant.current = true

      const buffered = [...(window.__twOsanoBlockedScripts ?? [])]
      window.__twOsanoBlockedScripts = []

      if (buffered.length > 0) {
        buffered.forEach((src) => {
          deliverOsanoEvent(RudderEvent.OSANO_SCRIPT_BLOCKED, { src })
        })
      }
    }
  }, [isOsanoCmpActive, analyticsConsentGranted])

  useEffect(() => {
    if (!isOsanoCmpActive) {
      return
    }

    const cleanup = subscribeToOsanoConsentEvents({
      onInitialized(consent: OsanoConsent | undefined) {
        if (consent === undefined) {
          // Banner is showing — no saved consent (strict / first visit)
          initializedWithNullConsent.current = true
        }

        // Banner may still be visible under implicit consent even when Osano
        // passes a consent object (ACCEPT defaults). Match WordPress: fire
        // when the bar is actually shown, not only when consent is undefined.
        if (consent === undefined || isOsanoBannerVisible()) {
          emitBannerDisplayedOnce()
        }

        if (!analyticsConsentGrantedRef.current) {
          return
        }

        // osano_consent_snapshot: once per tab session from initialized
        if (!isInitializedSnapshotSent()) {
          const snapshotConsent =
            consent ?? window.Osano?.cm?.getConsent?.() ?? {}
          deliverOsanoEvent(
            RudderEvent.OSANO_CONSENT_SNAPSHOT,
            buildConsentSnapshotPayload(
              snapshotConsent,
              initializedWithNullConsent.current
            ),
            () => {
              markInitializedSnapshotSent()
            }
          )
        }
      },

      onConsentSaved(consent: OsanoConsent) {
        if (consent.ANALYTICS !== 'ACCEPT') {
          return
        }

        // osano_consent_saved: every time, including replay on page load
        trackBufferedEvent(RudderEvent.OSANO_CONSENT_SAVED, consent)

        // osano_consent_snapshot: always send after a save (bypasses flag)
        trackBufferedEvent(
          RudderEvent.OSANO_CONSENT_SNAPSHOT,
          buildConsentSnapshotPayload(
            consent,
            initializedWithNullConsent.current
          )
        )
      },

      onUiChanged(component: string, stateChange: string) {
        // Banner show: detect via UI so we catch dialog open even if
        // initialized already fired with a consent object.
        if (
          (component === 'dialog' || component === 'banner') &&
          stateChange === 'show'
        ) {
          emitBannerDisplayedOnce()
        }

        if (!analyticsConsentGrantedRef.current) {
          return
        }

        if (component === 'drawer' && stateChange === 'show') {
          deliverOsanoEvent(RudderEvent.OSANO_PREFERENCES_DRAWER_OPENED, {
            component,
            stateChange,
          })
        }
      },

      onScriptBlocked(src: string) {
        if (!analyticsConsentGrantedRef.current) {
          return
        }

        removeBlockedScriptFromBuffer(src)
        deliverOsanoEvent(RudderEvent.OSANO_SCRIPT_BLOCKED, { src })
      },
    })

    return cleanup
  }, [isOsanoCmpActive])
}

export default useOsanoConsentEvents
